import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { ZebraPreview } from '@/components/ui/zebra-preview';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ProgressStepsCompact } from '@/components/ui/progress-steps';
import { EtiquetaPreviewDialog } from '@/components/EtiquetaPreview';
import { Plus, QrCode, Printer, Trash2, FileText, Brain, CheckCircle, XCircle, Loader2, Upload, FlaskConical, TestTube2, Send, Users, Warehouse, X, Tag, Wifi, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateTime, formatDate, formatTime } from '@/utils/dateFormat';
import { connectQZ, findArgoxPrinter, getAllPrinters, printRawPPLA, printPixelLabels, printTestPage, isQZActive } from '@/lib/qzTrayService';
import { useFuncionarios, FuncionarioSelect } from '@/components/FuncionarioSelect';

export default function CiclosPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ciclos, setCiclos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [autoclaves, setAutoclaves] = useState([]);
  const [montagensProntas, setMontagensProntas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCiclo, setSelectedCiclo] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [showTestesDialog, setShowTestesDialog] = useState(false);
  const [testeCicloId, setTesteCicloId] = useState(null);
  const [uploadingTeste, setUploadingTeste] = useState(false);
  const [testeResults, setTesteResults] = useState({ biologico: null, quimico: null });
  const [showClientesDialog, setShowClientesDialog] = useState(false);
  const [selectedCicloClientes, setSelectedCicloClientes] = useState(null);
  const [needsBowieDick, setNeedsBowieDick] = useState(false);
  const [showBowieDickDialog, setShowBowieDickDialog] = useState(false);
  const [bowieDickCicloId, setBowieDickCicloId] = useState(null);
  const [bowieDickResult, setBowieDickResult] = useState(null);
  const [uploadingBowieDick, setUploadingBowieDick] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printCicloId, setPrintCicloId] = useState(null);
  const [printQuantity, setPrintQuantity] = useState(1);
  const [printerType, setPrinterType] = useState('pdf'); // pdf, argox or zebra
  const [qzConnected, setQzConnected] = useState(false);
  const [qzPrinters, setQzPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [qzLoading, setQzLoading] = useState(false);
  const [showEtiquetaDialog, setShowEtiquetaDialog] = useState(false);
  const [etiquetaCiclo, setEtiquetaCiclo] = useState(null);
  const [etiquetaQuantidade, setEtiquetaQuantidade] = useState(1);
  const [etiquetaDiasValidade, setEtiquetaDiasValidade] = useState(7);
  const { user } = useAuth();
  const isAdmin = ['admin', 'administrador'].includes(user?.perfil || user?.role);
  const { funcionarios } = useFuncionarios();
  const [showHorariosDialog, setShowHorariosDialog] = useState(false);
  const [horariosCiclo, setHorariosCiclo] = useState(null);
  const [horariosEdit, setHorariosEdit] = useState({ hora_inicio: '', hora_fim: '' });
  const [montagemCaixasDetalhes, setMontagemCaixasDetalhes] = useState({}); // Map: cliente_id -> caixas[]
  const [montagemClienteId, setMontagemClienteId] = useState(null); // Track which client came from montagem
  const [usedMontagemIds, setUsedMontagemIds] = useState([]); // Track all used montagem IDs
  const [montagemDropdownValue, setMontagemDropdownValue] = useState(''); // Separate state for dropdown display
  const [formData, setFormData] = useState({
    autoclave_id: '',
    metodo: 'vapor',
    numero_ciclo: '',
    numero_biologico: '',
    clientes_ciclo: [],  // Array of {cliente_id, materiais_ids}
    caixas_ids: [],
    kits_ids: [],
    temperatura: 121,
    pressao: 1.5,
    tempo_minutos: 15,
    hora_inicio: '',
    hora_fim: '',
    montagem_id: '',
    is_bowie_dick: false,  // Flag for Bowie-Dick test cycle
    funcionario_id: '',
    funcionario_nome: '',
  });
  const [clienteAtual, setClienteAtual] = useState({
    cliente_id: '',
    materiais_ids: []
  });

  // Converter montagens prontas para o formato do SearchableSelect
  // Filtra montagens já utilizadas neste ciclo
  const montagensOptions = montagensProntas
    .filter(m => !usedMontagemIds.includes(m.id))
    .map(m => {
      const jaAdicionado = formData.clientes_ciclo.some(cc => cc.cliente_id === m.cliente_id);
      const numCaixas = m.quantidade_kits || (m.caixas ? m.caixas.length : 0);
      return {
        id: m.id,
        label: `${m.numero_montagem} - ${m.cliente_nome || 'Cliente'}${jaAdicionado ? ' (materiais serão mesclados)' : ''}`,
        sublabel: `${numCaixas} caixa(s) - ${formatDate(m.data_hora || m.created_at)}`
      };
    });

  // Converter clientes para o formato do SearchableSelect
  // Filtra clientes que já foram adicionados ao ciclo
  const clientesJaAdicionados = formData.clientes_ciclo.map(cc => cc.cliente_id);
  const clientesOptions = clientes
    .filter(c => !clientesJaAdicionados.includes(c.id))
    .map(c => ({
      id: c.id,
      label: c.nome,
      sublabel: `${c.cnpj_cpf || ''} ${c.telefone ? `| ${c.telefone}` : ''}`
    }));

  // Converter autoclaves para o formato do SearchableSelect
  const autoclavesOptions = autoclaves.filter(a => a.status === 'ativo').map(a => ({
    id: a.id,
    label: `${a.identificacao} - ${a.marca}`,
    sublabel: a.modelo || ''
  }));

  // Open clientes dialog for sending PDFs
  const handleOpenClientesPDF = (ciclo) => {
    setSelectedCicloClientes(ciclo);
    setShowClientesDialog(true);
  };

  // Download PDF for specific client
  const handleDownloadClientePDF = async (cicloId, clienteId, clienteNome) => {
    try {
      const response = await api.get(`/pdf/ciclo/${cicloId}/cliente/${clienteId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `esterilizacao-${clienteNome.replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`PDF do cliente ${clienteNome} baixado!`);
    } catch (error) {
      toast.error('Erro ao baixar PDF do cliente');
    }
  };

  // Send WhatsApp with link
  const handleSendWhatsApp = (cicloId, clienteNome, telefone) => {
    const mensagem = `Olá ${clienteNome}! Seu comprovante de esterilização está disponível. Acesse: ${window.location.origin}/consulta/${cicloId}`;
    const telefoneClean = telefone?.replace(/\D/g, '') || '';
    const whatsappUrl = `https://wa.me/55${telefoneClean}?text=${encodeURIComponent(mensagem)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Abrindo WhatsApp...');
  };

  // Send to Arsenal (Distribution)
  const handleSendToArsenal = async (cicloId) => {
    try {
      const response = await api.post(`/arsenal/add-from-ciclo/${cicloId}`);
      toast.success(response.data.message || 'Enviado para o Arsenal!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar para o Arsenal');
    }
  };

  // Check if autoclave needs Bowie-Dick test
  const checkBowieDick = async (autoclaveId) => {
    if (!autoclaveId) {
      setNeedsBowieDick(false);
      return;
    }
    try {
      const response = await api.get(`/autoclaves/${autoclaveId}/needs-bowie-dick`);
      setNeedsBowieDick(response.data.needs_bowie_dick);
      if (response.data.needs_bowie_dick) {
        toast.warning('⚠️ Esta autoclave precisa do Teste Bowie-Dick antes do primeiro ciclo do dia!');
      }
    } catch (error) {
      console.error('Erro ao verificar Bowie-Dick:', error);
    }
  };

  // Open Bowie-Dick dialog
  const handleOpenBowieDick = (cicloId) => {
    setBowieDickCicloId(cicloId);
    setBowieDickResult(null);
    setShowBowieDickDialog(true);
  };

  // Upload and analyze Bowie-Dick test
  const handleUploadBowieDick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBowieDick(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('lote_indicador', document.getElementById('bowie_lote')?.value || '');

      const response = await api.post(`/ai/analyze-bowie-dick/${bowieDickCicloId}`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setBowieDickResult(response.data);
      
      if (response.data.aprovado) {
        toast.success('Teste Bowie-Dick: APROVADO ✓');
      } else {
        toast.error('Teste Bowie-Dick: REPROVADO - Verifique a autoclave');
      }
      fetchData();
    } catch (error) {
      toast.error('Erro ao analisar teste Bowie-Dick');
      console.error(error);
    } finally {
      setUploadingBowieDick(false);
    }
  };

  // Open testes dialog
  const handleOpenTestes = (cicloId) => {
    setTesteCicloId(cicloId);
    setTesteResults({ biologico: null, quimico: null });
    setShowTestesDialog(true);
  };

  // Upload and analyze biological test
  const handleUploadTesteBiologico = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingTeste(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('lote_indicador', document.getElementById('lote_indicador')?.value || '');
      formData.append('tempo_incubacao', document.getElementById('tempo_incubacao')?.value || '48');

      const response = await api.post(`/ai/analyze-teste-biologico/${testeCicloId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setTesteResults(prev => ({ ...prev, biologico: response.data }));
      
      if (response.data.conformidade) {
        toast.success('Teste Biológico: NEGATIVO (Aprovado)');
      } else {
        toast.error('Teste Biológico: POSITIVO (Reprovado)');
      }
      fetchData(); // Refresh to get updated cycle
    } catch (error) {
      toast.error('Erro ao analisar teste biológico');
      console.error(error);
    } finally {
      setUploadingTeste(false);
    }
  };

  // Upload and analyze chemical test
  const handleUploadTesteQuimico = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingTeste(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('classe_indicador', document.getElementById('classe_indicador')?.value || '5');

      const response = await api.post(`/ai/analyze-teste-quimico/${testeCicloId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setTesteResults(prev => ({ ...prev, quimico: response.data }));
      
      if (response.data.conformidade) {
        toast.success('Teste Químico: APROVADO');
      } else {
        toast.error('Teste Químico: REPROVADO');
      }
      fetchData(); // Refresh to get updated cycle
    } catch (error) {
      toast.error('Erro ao analisar teste químico');
      console.error(error);
    } finally {
      setUploadingTeste(false);
    }
  };

  // AI Analysis function
  const handleAIAnalysis = async () => {
    setAnalyzingAI(true);
    setAiAnalysis(null);
    try {
      const response = await api.post('/ai/analyze-ciclo', {
        temperatura: formData.temperatura,
        pressao: formData.pressao,
        tempo_minutos: formData.tempo_minutos,
        metodo: formData.metodo
      });
      setAiAnalysis(response.data);
      if (response.data.conformidade) {
        toast.success('Parâmetros em conformidade com a RDC 15!');
      } else {
        toast.warning('Atenção: Verifique as recomendações da análise');
      }
    } catch (error) {
      toast.error('Erro ao analisar parâmetros');
      console.error(error);
    } finally {
      setAnalyzingAI(false);
    }
  };

  // Check for montagem param from URL (coming from Montagem page)
  useEffect(() => {
    const montagemId = searchParams.get('montagem');
    if (montagemId && clientes.length > 0 && materiais.length > 0) {
      // Load montagem data and open dialog
      const loadMontagem = async () => {
        try {
          const response = await api.get(`/montagens/${montagemId}`);
          const { montagem, itens } = response.data;
          
          if (!montagem) {
            toast.error('Montagem não encontrada');
            return;
          }
          
          // Extract ALL material IDs from caixas (assembled boxes) AND top-level itens
          const caixas = montagem.caixas || [];
          const materialIdsSet = new Set();
          const caixasIds = [];
          let totalItens = 0;
          const materiaisQtd = {}; // Track quantities per material
          
          // Build caixas details for display: [{id, nome, itens: [{material_id, quantidade, nome}]}]
          const caixasDetalhes = [];
          
          // Get materials from all caixas (primary source for assembled items)
          caixas.forEach((caixa, idx) => {
            const caixaId = caixa.id || `caixa-${idx}`;
            caixasIds.push(caixaId);
            const caixaItensDetalhes = [];
            (caixa.itens || []).forEach(item => {
              if (item.material_id) {
                materialIdsSet.add(item.material_id);
                const qty = item.quantidade || 1;
                materiaisQtd[item.material_id] = (materiaisQtd[item.material_id] || 0) + qty;
                const mat = materiais.find(m => m.id === item.material_id);
                caixaItensDetalhes.push({
                  material_id: item.material_id,
                  quantidade: qty,
                  nome: mat ? `${mat.codigo} - ${mat.nome}` : item.material_id
                });
              }
              totalItens += item.quantidade || 1;
            });
            caixasDetalhes.push({
              id: caixaId,
              nome: caixa.nome || `Caixa ${idx + 1}`,
              itens: caixaItensDetalhes
            });
          });
          
          // Also include top-level itens (materials from reception that might not be in caixas)
          const montagemItens = itens || montagem.itens || [];
          montagemItens.forEach(item => {
            if (item.material_id) {
              materialIdsSet.add(item.material_id);
              const qty = item.quantidade || 1;
              if (!materiaisQtd[item.material_id]) {
                materiaisQtd[item.material_id] = qty;
              }
            }
          });
          
          const materialIds = [...materialIdsSet];
          
          // Store caixas details keyed by cliente_id
          setMontagemCaixasDetalhes(prev => ({
            ...prev,
            [montagem.cliente_id]: caixasDetalhes
          }));
          setMontagemClienteId(montagem.cliente_id);
          
          // Create client entry for the cycle with all materials and quantities
          const novoCliente = {
            cliente_id: montagem.cliente_id,
            materiais_ids: materialIds,
            materiais_qtd: materiaisQtd,
            quantidade_kits: montagem.quantidade_kits || caixas.length || 0
          };
          
          // Update form with montagem data
          setFormData(prev => ({
            ...prev,
            clientes_ciclo: [novoCliente],
            montagem_id: montagemId,
            caixas_ids: caixasIds,
            quantidade_kits: montagem.quantidade_kits || caixas.length || 1
          }));
          
          // Open the dialog
          setIsDialogOpen(true);
          
          // Track montagem as used so it doesn't appear in dropdown
          setUsedMontagemIds(prev => [...prev, montagemId]);
          
          // Clear the URL param
          setSearchParams({});
          
          const numKits = montagem.quantidade_kits || caixas.length || 1;
          toast.success(`Montagem carregada: ${numKits} caixa(s), ${materialIds.length} tipo(s) de material, ${totalItens} itens total.`);
        } catch (error) {
          console.error('Erro ao carregar montagem:', error);
          toast.error('Erro ao carregar dados da montagem');
        }
      };
      
      loadMontagem();
    }
  }, [searchParams, clientes, materiais, setSearchParams]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ciclosRes, clientesRes, materiaisRes, autoclavesRes, montagensRes] = await Promise.all([
        api.get('/ciclos'),
        api.get('/clientes'),
        api.get('/materiais'),
        api.get('/autoclaves'),
        api.get('/montagens'),
      ]);
      setCiclos(ciclosRes.data);
      setClientes(clientesRes.data);
      setMateriais(materiaisRes.data);
      setAutoclaves(autoclavesRes.data);
      // Filter montagens with status "pronto" (ready for sterilization)
      setMontagensProntas(montagensRes.data.filter(m => m.status === 'pronto'));
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleMontagemSelectChange = async (e) => {
    const montagemId = e.target.value;
    if (!montagemId) return;
    
    // Prevent re-selection of already used montagens
    if (usedMontagemIds.includes(montagemId)) {
      toast.warning('Esta montagem já foi adicionada ao ciclo');
      setMontagemDropdownValue('');
      return;
    }

    try {
      const response = await api.get(`/montagens/${montagemId}`);
      const { montagem, itens } = response.data;
      
      if (!montagem) {
        toast.error('Montagem não encontrada');
        return;
      }
      
      // Extract materials from caixas AND top-level itens (same logic as loadMontagem)
      const caixas = montagem.caixas || [];
      const materialIdsSet = new Set();
      const caixasIds = [];
      const materiaisQtd = {};
      let totalItens = 0;
      
      const caixasDetalhes = [];
      
      caixas.forEach((caixa, idx) => {
        const caixaId = caixa.id || `caixa-${idx}`;
        caixasIds.push(caixaId);
        const caixaItensDetalhes = [];
        (caixa.itens || []).forEach(item => {
          if (item.material_id) {
            materialIdsSet.add(item.material_id);
            const qty = item.quantidade || 1;
            materiaisQtd[item.material_id] = (materiaisQtd[item.material_id] || 0) + qty;
            const mat = materiais.find(m => m.id === item.material_id);
            caixaItensDetalhes.push({
              material_id: item.material_id,
              quantidade: qty,
              nome: mat ? `${mat.codigo} - ${mat.nome}` : item.material_id
            });
          }
          totalItens += item.quantidade || 1;
        });
        caixasDetalhes.push({ id: caixaId, nome: caixa.nome || `Caixa ${idx + 1}`, itens: caixaItensDetalhes });
      });
      
      // Also include top-level itens
      const montagemItens = itens || montagem.itens || [];
      montagemItens.forEach(item => {
        if (item.material_id) {
          materialIdsSet.add(item.material_id);
          if (!materiaisQtd[item.material_id]) {
            materiaisQtd[item.material_id] = item.quantidade || 1;
          }
        }
      });
      
      const materialIds = [...materialIdsSet];
      const numKits = montagem.quantidade_kits || caixas.length || 0;
      
      // Store caixas details for this client
      setMontagemCaixasDetalhes(prev => ({
        ...prev,
        [montagem.cliente_id]: caixasDetalhes
      }));
      
      // Add/merge client
      setFormData(prev => {
        const clienteIndex = prev.clientes_ciclo.findIndex(c => c.cliente_id === montagem.cliente_id);
        
        if (clienteIndex >= 0) {
          // Client already exists → MERGE materials and quantities
          const existing = prev.clientes_ciclo[clienteIndex];
          const mergedMateriais = [...new Set([...existing.materiais_ids, ...materialIds])];
          const mergedQtd = { ...(existing.materiais_qtd || {}) };
          Object.entries(materiaisQtd).forEach(([matId, qty]) => {
            mergedQtd[matId] = (mergedQtd[matId] || 0) + qty;
          });
          const updatedClientes = [...prev.clientes_ciclo];
          updatedClientes[clienteIndex] = {
            ...existing,
            materiais_ids: mergedMateriais,
            materiais_qtd: mergedQtd,
            quantidade_kits: (existing.quantidade_kits || 0) + numKits
          };
          return { ...prev, montagem_id: montagemId, caixas_ids: [...(prev.caixas_ids || []), ...caixasIds], clientes_ciclo: updatedClientes };
        }
        
        // New client
        const novoCliente = {
          cliente_id: montagem.cliente_id,
          materiais_ids: materialIds,
          materiais_qtd: materiaisQtd,
          quantidade_kits: numKits
        };
        return {
          ...prev,
          montagem_id: montagemId,
          caixas_ids: [...(prev.caixas_ids || []), ...caixasIds],
          clientes_ciclo: [...prev.clientes_ciclo, novoCliente]
        };
      });
      
      // Track as used
      setUsedMontagemIds(prev => [...prev, montagemId]);
      // Clear dropdown display (formData.montagem_id stays for backend submission)
      setMontagemDropdownValue('');
      setClienteAtual({ cliente_id: '', materiais_ids: [] });
      
      const clienteNome = clientes.find(c => c.id === montagem.cliente_id)?.nome || 'Cliente';
      const clienteExiste = formData.clientes_ciclo.some(c => c.cliente_id === montagem.cliente_id);
      if (clienteExiste) {
        toast.success(`Materiais da montagem mesclados ao ${clienteNome} (${numKits} caixa(s))`);
      } else {
        toast.success(`${clienteNome} adicionado: ${numKits} caixa(s), ${materialIds.length} tipo(s) de material`);
      }
    } catch (error) {
      console.error('Erro ao carregar montagem:', error);
      toast.error('Erro ao carregar dados da montagem');
    }
  };

  const handleAdicionarCliente = () => {
    if (!clienteAtual.cliente_id || clienteAtual.materiais_ids.length === 0) {
      toast.error('Selecione um cliente e pelo menos um material');
      return;
    }

    // Build quantity map: each manually added material counts as 1
    const newQtd = {};
    clienteAtual.materiais_ids.forEach(matId => {
      newQtd[matId] = (newQtd[matId] || 0) + 1;
    });

    // Check if client already exists → merge materials and quantities
    const clienteIndex = formData.clientes_ciclo.findIndex(c => c.cliente_id === clienteAtual.cliente_id);
    if (clienteIndex >= 0) {
      setFormData(prev => {
        const existingClient = prev.clientes_ciclo[clienteIndex];
        const mergedMateriais = [...new Set([...existingClient.materiais_ids, ...clienteAtual.materiais_ids])];
        const mergedQtd = { ...(existingClient.materiais_qtd || {}) };
        Object.entries(newQtd).forEach(([matId, qty]) => {
          mergedQtd[matId] = (mergedQtd[matId] || 0) + qty;
        });
        const updatedClientes = [...prev.clientes_ciclo];
        updatedClientes[clienteIndex] = { ...existingClient, materiais_ids: mergedMateriais, materiais_qtd: mergedQtd };
        return { ...prev, clientes_ciclo: updatedClientes };
      });
      setClienteAtual({ cliente_id: '', materiais_ids: [] });
      toast.success('Materiais adicionados ao cliente existente!');
      return;
    }

    setFormData(prev => ({
      ...prev,
      clientes_ciclo: [...prev.clientes_ciclo, { ...clienteAtual, materiais_qtd: newQtd }]
    }));

    setClienteAtual({ cliente_id: '', materiais_ids: [] });
    toast.success('Cliente adicionado ao ciclo!');
  };

  const handleRemoverCliente = (index) => {
    // Also clean up montagem caixas details if removing the montagem client
    const removedClient = formData.clientes_ciclo[index];
    if (removedClient && montagemCaixasDetalhes[removedClient.cliente_id]) {
      setMontagemCaixasDetalhes(prev => {
        const updated = { ...prev };
        delete updated[removedClient.cliente_id];
        return updated;
      });
    }
    
    setFormData(prev => ({
      ...prev,
      clientes_ciclo: prev.clientes_ciclo.filter((_, i) => i !== index)
    }));
    toast.success('Cliente removido do ciclo');
  };

  const handleMaterialToggleAtual = (id) => {
    const current = [...clienteAtual.materiais_ids];
    if (current.includes(id)) {
      setClienteAtual({ ...clienteAtual, materiais_ids: current.filter(m => m !== id) });
    } else {
      setClienteAtual({ ...clienteAtual, materiais_ids: [...current, id] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.clientes_ciclo.length === 0) {
      toast.error('Adicione pelo menos um cliente ao ciclo');
      return;
    }
    
    if (!formData.autoclave_id) {
      toast.error('Selecione uma autoclave para o ciclo');
      return;
    }
    
    if (!formData.funcionario_id) {
      toast.error('Selecione o funcionário responsável pelo ciclo');
      return;
    }
    
    try {
      const data = {
        ...formData,
        profissional_id: user.id,
      };
      await api.post('/ciclos', data);
      toast.success('Ciclo criado com sucesso!');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Erro ao criar ciclo:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : (detail?.[0]?.msg || error.message || 'Erro desconhecido');
      toast.error('Erro ao criar ciclo: ' + errorMsg);
    }
  };

  const handleResultado = async (cicloId, resultado) => {
    try {
      await api.put(`/ciclos/${cicloId}/resultado`, { resultado });
      toast.success(`Ciclo ${resultado === 'aprovado' ? 'aprovado' : 'reprovado'}!`);
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar resultado');
    }
  };

  const handleDeleteCiclo = async (cicloId) => {
    if (!window.confirm('Deseja excluir este ciclo?')) return;
    try {
      await api.delete(`/ciclos/${cicloId}`);
      toast.success('Ciclo excluído com sucesso');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir ciclo');
    }
  };

  const handlePrintZPL = async (cicloId) => {
    try {
      const response = await api.get(`/etiquetas/${cicloId}/zpl`);
      const zpl = response.data.zpl;
      
      // Create a blob and download
      const blob = new Blob([zpl], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `etiqueta-${response.data.ciclo_id}.zpl`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Arquivo ZPL gerado! Envie para a impressora Zebra.');
    } catch (error) {
      toast.error('Erro ao gerar ZPL');
    }
  };

  // Open print dialog for Argox
  const handleOpenPrintDialog = (cicloId) => {
    setPrintCicloId(cicloId);
    setPrintQuantity(1);
    setPrinterType('pdf');
    setShowPrintDialog(true);
  };

  // Connect to QZ Tray and find printers
  const handleConnectQZ = async () => {
    setQzLoading(true);
    try {
      await connectQZ();
      setQzConnected(true);
      
      const printers = await getAllPrinters();
      setQzPrinters(printers);
      
      // Try to auto-select Argox
      const argox = await findArgoxPrinter();
      if (argox) {
        setSelectedPrinter(argox);
        toast.success(`Impressora encontrada: ${argox}`);
      } else {
        toast.info('Selecione sua impressora Argox na lista');
      }
    } catch (err) {
      setQzConnected(false);
      toast.error(err.message || 'Erro ao conectar ao QZ Tray');
    } finally {
      setQzLoading(false);
    }
  };

  // Test print - sends test image via pixel printing through printer driver
  const handleTestPrint = async () => {
    if (!selectedPrinter) {
      toast.error('Selecione uma impressora primeiro');
      return;
    }
    try {
      toast.info('Gerando teste de impressão...');
      const response = await api.get('/etiquetas/teste-impressao');
      const { imagem, largura_mm, altura_mm } = response.data;
      
      toast.info('Enviando para a impressora via driver...');
      await printTestPage(selectedPrinter, imagem, { width: largura_mm, height: altura_mm });
      toast.success('Teste enviado com sucesso! Verifique se a etiqueta saiu na impressora.');
    } catch (err) {
      toast.error(`Erro no teste: ${err.message}`);
    }
  };

  // Print Argox via QZ Tray - PIXEL MODE (uses printer driver like BarTender)
  const handlePrintArgoxQZ = async () => {
    if (!selectedPrinter) {
      toast.error('Selecione uma impressora primeiro');
      return;
    }
    
    try {
      toast.info('Gerando etiquetas...');
      
      // Get label images from backend (rendered as PNG)
      const response = await api.get(`/etiquetas/ciclo/${printCicloId}/imagem`, {
        params: { quantidade: printQuantity }
      });
      
      const { imagens, largura_mm, altura_mm, dpi } = response.data;
      
      toast.info(`Enviando ${imagens.length} etiqueta(s) para ${selectedPrinter}...`);
      
      // Send images via pixel printing (through printer driver, like BarTender)
      await printPixelLabels(selectedPrinter, imagens, { width: largura_mm, height: altura_mm }, dpi || 203);
      
      setShowPrintDialog(false);
      toast.success(`${imagens.length} etiqueta(s) impressa(s) na ${selectedPrinter}!`);
    } catch (err) {
      toast.error(`Erro na impressão: ${err.message}`);
    }
  };

  // Print Argox O214 (TSPL) - download .PRN file (fallback)
  const handlePrintArgoxDownload = async () => {
    try {
      // Download as blob since endpoint returns raw TSPL content
      const response = await api.get(`/etiquetas/ciclo/${printCicloId}/tspl`, {
        responseType: 'blob'
      });
      
      // Create a blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `etiquetas-argox-${printCicloId.substring(0, 8)}.prn`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      setShowPrintDialog(false);
      toast.success('Arquivo .PRN gerado! Envie para a impressora Argox via comando: copy /b arquivo.prn USB001');
    } catch (error) {
      toast.error('Erro ao gerar etiquetas Argox');
    }
  };

  // Print Zebra (ZPL) with quantity
  // Print Zebra via QZ Tray (direct raw ZPL) or download fallback
  const handlePrintZebra = async () => {
    if (qzConnected && selectedPrinter) {
      // QZ Tray direct print
      try {
        toast.info('Enviando etiquetas ZPL para a impressora...');
        const response = await api.get(`/etiquetas/ciclo/${printCicloId}/zpl`, {
          responseType: 'text'
        });
        const zplCommands = typeof response.data === 'string'
          ? response.data
          : new TextDecoder().decode(response.data);
        await printRawPPLA(selectedPrinter, zplCommands);
        setShowPrintDialog(false);
        toast.success(`Etiqueta(s) impressa(s) na ${selectedPrinter}!`);
      } catch (err) {
        toast.error(`Erro na impressão: ${err.message}`);
      }
    } else {
      // Download fallback
      try {
        const response = await api.get(`/etiquetas/ciclo/${printCicloId}/zpl`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `etiquetas-zebra-${printCicloId.substring(0, 8)}.zpl`;
        a.click();
        window.URL.revokeObjectURL(url);
        setShowPrintDialog(false);
        toast.success('Arquivo ZPL gerado! Envie para a impressora Zebra.');
      } catch (error) {
        toast.error('Erro ao gerar etiquetas Zebra');
      }
    }
  };

  // Print PDF with QR Code
  const handlePrintPDF = async () => {
    try {
      toast.info('Gerando PDF com etiquetas...');
      const response = await api.get(`/etiquetas/ciclo/${printCicloId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `etiquetas-${printCicloId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setShowPrintDialog(false);
      toast.success(`PDF com ${printQuantity} etiqueta(s) gerado com sucesso! Imprima em qualquer impressora.`);
    } catch (error) {
      toast.error('Erro ao gerar PDF de etiquetas');
    }
  };

  // Handle print based on selected printer type
  const handlePrint = () => {
    if (printerType === 'argox') {
      if (qzConnected && selectedPrinter) {
        handlePrintArgoxQZ();
      } else {
        handlePrintArgoxDownload();
      }
    } else if (printerType === 'zebra') {
      handlePrintZebra();
    } else {
      handlePrintPDF();
    }
  };

  const handleShowQR = (ciclo) => {
    setSelectedCiclo(ciclo);
    setShowQR(true);
  };

  // Open horários edit dialog
  const handleOpenHorarios = (ciclo) => {
    setHorariosCiclo(ciclo);
    setHorariosEdit({ hora_inicio: ciclo.hora_inicio || '', hora_fim: ciclo.hora_fim || '' });
    setShowHorariosDialog(true);
  };

  // Save horários
  const handleSaveHorarios = async () => {
    if (!horariosCiclo) return;
    try {
      await api.put(`/ciclos/${horariosCiclo.id}/horarios`, horariosEdit);
      toast.success('Horários atualizados!');
      setShowHorariosDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar horários');
    }
  };

  const resetForm = () => {
    setFormData({
      autoclave_id: '',
      metodo: 'vapor',
      numero_ciclo: '',
      numero_biologico: '',
      clientes_ciclo: [],
      caixas_ids: [],
      kits_ids: [],
      temperatura: 121,
      pressao: 1.5,
      tempo_minutos: 15,
      hora_inicio: '',
      hora_fim: '',
      montagem_id: '',
      is_bowie_dick: false,
      funcionario_id: '',
      funcionario_nome: '',
    });
    setClienteAtual({ cliente_id: '', materiais_ids: [] });
    setMontagemCaixasDetalhes({});
    setMontagemClienteId(null);
    setMontagemDropdownValue('');
    setUsedMontagemIds([]);
    setNeedsBowieDick(false);
  };

  const handleDownloadPDF = async (cicloId, clienteId = null) => {
    try {
      const url = clienteId 
        ? `/pdf/ciclo/${cicloId}/cliente/${clienteId}`
        : `/pdf/ciclo/${cicloId}`;
      
      const response = await api.get(url, { responseType: 'blob' });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `ciclo-${cicloId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  const getClienteNome = (id) => clientes.find(c => c.id === id)?.nome || 'N/A';

  return (
    <div data-testid="ciclos-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ciclos de Esterilização
          </h1>
          <p className="text-lg text-slate-600 mt-2">Gerenciar ciclos e etiquetas</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="btn-add-ciclo"
              onClick={() => resetForm()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Ciclo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => {
            if (e.target.closest('#searchable-select-portal') || document.getElementById('searchable-select-portal')?.contains(e.target)) {
              e.preventDefault();
            }
          }} onInteractOutside={(e) => {
            if (document.getElementById('searchable-select-portal')) {
              e.preventDefault();
            }
          }}>
            <DialogHeader>
              <DialogTitle>Novo Ciclo de Esterilização</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <Label className="text-blue-900 font-semibold">Selecionar Montagem (Opcional)</Label>
                  <div className="mt-2">
                    <SearchableSelect
                      options={montagensOptions}
                      value={montagemDropdownValue}
                      onChange={(value) => {
                        setMontagemDropdownValue(value);
                        handleMontagemSelectChange({ target: { value } });
                      }}
                      placeholder={usedMontagemIds.length > 0 ? "Adicionar outra montagem..." : "Criar ciclo manualmente (sem montagem)"}
                      searchPlaceholder="Buscar por código ou cliente..."
                      emptyMessage="Nenhuma montagem pronta para esterilizar"
                    />
                  </div>
                  {usedMontagemIds.length > 0 && (
                    <p className="text-xs text-blue-700 mt-2">✓ {usedMontagemIds.length} montagem(ns) carregada(s) — clientes e materiais adicionados automaticamente</p>
                  )}
                </div>

                <div>
                  <Label>Cliente {usedMontagemIds.length > 0 && <span className="text-xs text-blue-600">(adicione mais clientes ao ciclo)</span>}</Label>
                  <SearchableSelect
                    options={clientesOptions}
                    value={clienteAtual.cliente_id}
                    onChange={(value) => setClienteAtual({ ...clienteAtual, cliente_id: value })}
                    placeholder="Selecione um cliente..."
                    searchPlaceholder="Buscar por nome, CPF/CNPJ ou telefone..."
                    emptyMessage="Nenhum cliente encontrado"
                  />
                </div>
                <div>
                  <Label>Autoclave</Label>
                  <SearchableSelect
                    options={autoclavesOptions}
                    value={formData.autoclave_id}
                    onChange={(value) => {
                      setFormData({ ...formData, autoclave_id: value });
                      checkBowieDick(value);
                    }}
                    placeholder="Selecione a autoclave..."
                    searchPlaceholder="Buscar por identificação..."
                    emptyMessage="Nenhuma autoclave ativa"
                  />
                  {needsBowieDick && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded-md">
                      <p className="text-xs text-amber-800 font-semibold flex items-center gap-1">
                        <TestTube2 className="w-4 h-4" />
                        Teste Bowie-Dick necessário! Primeiro ciclo do dia nesta autoclave.
                      </p>
                    </div>
                  )}
                </div>

                {/* Funcionário Responsável */}
                <div className="col-span-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <FuncionarioSelect
                    value={formData.funcionario_id}
                    onChange={(id, nome) => setFormData(prev => ({ ...prev, funcionario_id: id, funcionario_nome: nome }))}
                    funcionarios={funcionarios}
                  />
                </div>

                <div>
                  <Label htmlFor="numero_ciclo">Número do Ciclo</Label>
                  <Input
                    data-testid="input-numero-ciclo"
                    id="numero_ciclo"
                    placeholder="Ex: 001, CIC-001..."
                    value={formData.numero_ciclo}
                    onChange={(e) => setFormData({ ...formData, numero_ciclo: e.target.value })}
                  />
                  <p className="text-xs text-slate-400 mt-0.5">Deixe vazio para gerar automaticamente</p>
                </div>
                <div>
                  <Label htmlFor="numero_biologico">Número do Biológico</Label>
                  <Input
                    data-testid="input-numero-biologico"
                    id="numero_biologico"
                    placeholder="Ex: BIO-2026-001"
                    value={formData.numero_biologico}
                    onChange={(e) => setFormData({ ...formData, numero_biologico: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="metodo">Método</Label>
                  <select
                    data-testid="select-metodo"
                    id="metodo"
                    value={formData.metodo}
                    onChange={(e) => setFormData({ ...formData, metodo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value="vapor">Vapor</option>
                    <option value="oxido_etileno">Óxido de Etileno</option>
                    <option value="peroxido_hidrogenio">Peróxido de Hidrogênio</option>
                    <option value="formaldeido">Formaldeído</option>
                  </select>
                </div>

                {/* Bowie-Dick Test Checkbox */}
                {needsBowieDick && (
                  <div className="col-span-2 p-4 bg-amber-50 border-2 border-amber-400 rounded-lg">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_bowie_dick"
                        data-testid="checkbox-bowie-dick"
                        checked={formData.is_bowie_dick}
                        onChange={(e) => setFormData({ ...formData, is_bowie_dick: e.target.checked })}
                        className="w-5 h-5 text-amber-600 border-amber-400 rounded focus:ring-amber-500"
                      />
                      <Label htmlFor="is_bowie_dick" className="text-amber-900 font-semibold cursor-pointer flex items-center gap-2">
                        <TestTube2 className="w-5 h-5" />
                        Este é um Ciclo de Teste Bowie-Dick
                      </Label>
                    </div>
                    <p className="text-xs text-amber-700 mt-2 ml-8">
                      Marque esta opção se este ciclo é exclusivamente para realizar o teste Bowie-Dick. 
                      Após criar o ciclo, você poderá fazer upload da imagem do teste para análise por IA.
                    </p>
                  </div>
                )}
                <div>
                  <Label htmlFor="temperatura">Temperatura (°C)</Label>
                  <Input
                    data-testid="input-temperatura"
                    id="temperatura"
                    type="number" onFocus={(e) => e.target.select()}
                    step="0.1"
                    value={formData.temperatura}
                    onChange={(e) => setFormData({ ...formData, temperatura: parseFloat(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="pressao">Pressão (bar)</Label>
                  <Input
                    data-testid="input-pressao"
                    id="pressao"
                    type="number" onFocus={(e) => e.target.select()}
                    step="0.1"
                    value={formData.pressao}
                    onChange={(e) => setFormData({ ...formData, pressao: parseFloat(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="tempo_minutos">Tempo (minutos)</Label>
                  <Input
                    data-testid="input-tempo"
                    id="tempo_minutos"
                    type="number" onFocus={(e) => e.target.select()}
                    value={formData.tempo_minutos}
                    onChange={(e) => setFormData({ ...formData, tempo_minutos: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="hora_inicio">Hora de Início</Label>
                  <Input
                    data-testid="input-hora-inicio"
                    id="hora_inicio"
                    type="time"
                    value={formData.hora_inicio}
                    onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="hora_fim">Hora de Fim</Label>
                  <Input
                    data-testid="input-hora-fim"
                    id="hora_fim"
                    type="time"
                    value={formData.hora_fim}
                    onChange={(e) => setFormData({ ...formData, hora_fim: e.target.value })}
                  />
                </div>
              </div>

              {/* CME AI Assistant Card */}
              <div className="col-span-2 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-4 text-white">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Brain className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">CME AI Assistant</h3>
                      <p className="text-sm text-blue-100">
                        Validar parâmetros contra a <strong>RDC 15 ANVISA</strong> em tempo real
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={handleAIAnalysis}
                    disabled={analyzingAI}
                    className="bg-white text-blue-600 hover:bg-blue-50 font-semibold"
                  >
                    {analyzingAI ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analisando...
                      </>
                    ) : (
                      'Analisar Ciclo Agora'
                    )}
                  </Button>
                </div>
                
                {/* AI Analysis Results */}
                {aiAnalysis && (
                  <div className="mt-4 bg-white rounded-lg p-4 text-slate-900">
                    <div className="flex items-center gap-2 mb-3">
                      {aiAnalysis.conformidade ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-semibold ${aiAnalysis.conformidade ? 'text-green-600' : 'text-red-600'}`}>
                        {aiAnalysis.conformidade ? 'Em Conformidade' : 'Não Conforme'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 mb-3">{aiAnalysis.analise}</p>
                    {aiAnalysis.recomendacoes && aiAnalysis.recomendacoes.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-slate-600 mb-1">Recomendações:</p>
                        <ul className="text-xs text-slate-600 list-disc list-inside">
                          {aiAnalysis.recomendacoes.map((rec, idx) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-2">Ref: {aiAnalysis.norma_referencia}</p>
                  </div>
                )}
              </div>

              {/* Seção de confirmação de clientes adicionados */}
              {formData.clientes_ciclo.length > 0 && (
                <div className="col-span-2 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">
                      {formData.clientes_ciclo.length} cliente(s) adicionado(s) ao ciclo
                    </span>
                  </div>
                  <p className="text-sm text-green-700">
                    Os materiais foram automaticamente incluídos. Veja a lista abaixo.
                  </p>
                </div>
              )}

              {/* Seção para adicionar cliente manualmente - SEMPRE VISÍVEL */}
              <div className="col-span-2 border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50/50">
                <div className="flex items-center gap-2 mb-3">
                  <Plus className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-900">
                    {formData.clientes_ciclo.length === 0 ? 'Adicionar Cliente ao Ciclo' : 'Adicionar Mais Clientes ao Ciclo'}
                  </h4>
                </div>
                
                <div className="space-y-3">
                  {/* Seleção de cliente (só mostra se não foi via montagem) */}
                  {!clienteAtual.cliente_id && (
                    <div>
                      <Label className="text-sm text-blue-800">Selecione um cliente:</Label>
                      <SearchableSelect
                        options={clientesOptions}
                        value={clienteAtual.cliente_id}
                        onChange={(value) => setClienteAtual({ ...clienteAtual, cliente_id: value })}
                        placeholder="Buscar cliente..."
                        searchPlaceholder="Buscar por nome, CPF/CNPJ..."
                        emptyMessage="Nenhum cliente disponível"
                      />
                    </div>
                  )}
                  
                  {/* Cliente selecionado */}
                  {clienteAtual.cliente_id && (
                    <div className="bg-white p-3 rounded-md border border-blue-200">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-blue-900">
                          {clientes.find(c => c.id === clienteAtual.cliente_id)?.nome || 'Cliente'}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setClienteAtual({ cliente_id: '', materiais_ids: [] })}
                          className="text-slate-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Seleção de materiais */}
                  <div>
                    <Label className="text-sm text-blue-800">Materiais para este cliente:</Label>
                    <div className="mt-2 max-h-32 overflow-y-auto border border-slate-200 rounded-md p-2 space-y-1 bg-white">
                      {materiais.map(material => (
                        <label key={material.id} className="flex items-center space-x-2 p-1 hover:bg-slate-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={clienteAtual.materiais_ids.includes(material.id)}
                            onChange={() => handleMaterialToggleAtual(material.id)}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm text-slate-700">{material.codigo} - {material.nome}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      {clienteAtual.materiais_ids.length} material(is) selecionado(s)
                    </p>
                  </div>
                  
                  {/* Botão para adicionar */}
                  <Button
                    type="button"
                    onClick={handleAdicionarCliente}
                    disabled={!clienteAtual.cliente_id || clienteAtual.materiais_ids.length === 0}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Este Cliente ao Ciclo
                  </Button>
                </div>
              </div>

              {/* Lista de Clientes Adicionados */}
              {formData.clientes_ciclo.length > 0 && (
                <div className="col-span-2 border-t-2 border-slate-300 pt-4">
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Clientes no Ciclo ({formData.clientes_ciclo.length})
                  </h3>
                  <div className="space-y-3">
                    {formData.clientes_ciclo.map((clienteCiclo, index) => {
                      const cliente = clientes.find(c => c.id === clienteCiclo.cliente_id);
                      const clienteCaixas = montagemCaixasDetalhes[clienteCiclo.cliente_id] || [];
                      const clienteKits = clienteCiclo.quantidade_kits || (clienteCiclo.cliente_id === montagemClienteId ? formData.quantidade_kits : 0);
                      return (
                        <div key={index} className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-semibold text-blue-900 text-lg">{cliente?.nome || 'Cliente'}</div>
                              <div className="text-sm text-blue-700 mt-1 font-medium">
                                ✓ {clienteCiclo.materiais_ids.length} tipo(s) de material
                                {clienteKits > 0 && (
                                  <span className="ml-2 bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-bold">
                                    {clienteKits} caixa(s)/kit(s)
                                  </span>
                                )}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {clienteCiclo.materiais_ids.map(matId => {
                                  const mat = materiais.find(m => m.id === matId);
                                  const qty = clienteCiclo.materiais_qtd?.[matId] || 0;
                                  const nome = mat ? `${mat.codigo} - ${mat.nome}` : `ID: ${matId.substring(0, 8)}...`;
                                  return (
                                    <span key={matId} className={`text-xs px-2 py-1 rounded inline-flex items-center gap-1 ${mat ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-700'}`}>
                                      {nome}
                                      {qty > 0 && (
                                        <span className="bg-blue-600 text-white px-1.5 py-0 rounded-full text-[10px] font-bold ml-0.5">
                                          x{qty}
                                        </span>
                                      )}
                                    </span>
                                  );
                                })}
                              </div>
                              
                              {/* Detalhes das Caixas da Montagem - SOMENTE para o cliente que veio da montagem */}
                              {clienteCaixas.length > 0 && (
                                <div className="mt-3 border-t border-blue-200 pt-3">
                                  <p className="text-xs font-bold text-blue-900 mb-2 flex items-center gap-1">
                                    <Warehouse className="w-3.5 h-3.5" />
                                    Caixas da Montagem ({clienteCaixas.length}):
                                  </p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                    {clienteCaixas.map((caixa, cIdx) => (
                                      <div key={caixa.id} className="bg-white border border-blue-100 rounded p-2 text-xs">
                                        <div className="font-semibold text-slate-700 mb-1">
                                          Caixa {cIdx + 1}
                                        </div>
                                        <div className="space-y-0.5">
                                          {caixa.itens.map((item, iIdx) => (
                                            <div key={iIdx} className="text-slate-600 flex justify-between">
                                              <span className="truncate mr-1">{item.nome}</span>
                                              <span className="text-slate-400 font-medium flex-shrink-0">x{item.quantidade}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => handleRemoverCliente(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button data-testid="btn-submit" type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Criar Ciclo
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Número</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cliente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Método</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Data/Hora</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Progresso</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Resultado</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Testes</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {ciclos.map((ciclo) => (
              <tr key={ciclo.id} data-testid={`ciclo-row-${ciclo.id}`} className="hover:bg-slate-50">
                <td className="px-6 py-4 text-sm font-medium text-slate-900">
                  <div className="flex items-center gap-2">
                    {ciclo.numero_ciclo}
                    {ciclo.is_bowie_dick && (
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                        B&D
                      </span>
                    )}
                  </div>
                  {ciclo.numero_biologico && (
                    <div className="text-xs text-slate-400 mt-0.5">Bio: <span className="font-medium text-slate-600">{ciclo.numero_biologico}</span></div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">{getClienteNome(ciclo.cliente_id)}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{ciclo.metodo}</td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  <div>{formatDateTime(ciclo.data_hora)}</div>
                  {(ciclo.hora_inicio || ciclo.hora_fim) && (
                    <div className="text-xs mt-0.5 text-slate-400">
                      {ciclo.hora_inicio && <span>Início: <b className="text-slate-600">{ciclo.hora_inicio}</b></span>}
                      {ciclo.hora_inicio && ciclo.hora_fim && <span className="mx-1">|</span>}
                      {ciclo.hora_fim && <span>Fim: <b className="text-slate-600">{ciclo.hora_fim}</b></span>}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-sm">
                  <ProgressStepsCompact currentStatus={ciclo.resultado === 'aprovado' ? 'aprovado' : 'em_processo'} />
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={ciclo.resultado} />
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-1 flex-wrap">
                    {ciclo.is_bowie_dick && (
                      ciclo.teste_bowie_dick ? (
                        <span className={`px-2 py-1 text-xs rounded-full ${ciclo.teste_bowie_dick.resultado === 'aprovado' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          B&D: {ciclo.teste_bowie_dick.resultado === 'aprovado' ? '✓' : '✗'}
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">B&D: Pendente</span>
                      )
                    )}
                    {ciclo.teste_biologico ? (
                      <span className={`px-2 py-1 text-xs rounded-full ${ciclo.teste_biologico.conformidade ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        Bio: {ciclo.teste_biologico.resultado === 'negativo' ? '✓' : '✗'}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Bio: -</span>
                    )}
                    {ciclo.teste_quimico ? (
                      <span className={`px-2 py-1 text-xs rounded-full ${ciclo.teste_quimico.conformidade ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        Quím: {ciclo.teste_quimico.resultado === 'aprovado' ? '✓' : '✗'}
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Quím: -</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex gap-2 flex-wrap">
                    {ciclo.resultado === 'pendente' && (
                      <>
                        <Button
                          data-testid={`btn-approve-${ciclo.id}`}
                          size="sm"
                          onClick={() => handleResultado(ciclo.id, 'aprovado')}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          title="Aprovar este ciclo de esterilização"
                        >
                          Aprovar
                        </Button>
                        <Button
                          data-testid={`btn-reject-${ciclo.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleResultado(ciclo.id, 'reprovado')}
                          className="text-red-600 hover:text-red-700"
                          title="Reprovar este ciclo de esterilização"
                        >
                          Reprovar
                        </Button>
                      </>
                    )}
                    {ciclo.resultado === 'aprovado' && (
                      <Button
                        data-testid={`btn-arsenal-${ciclo.id}`}
                        size="sm"
                        onClick={() => handleSendToArsenal(ciclo.id)}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                        title="Enviar materiais para o Arsenal de Distribuição"
                      >
                        <Warehouse className="w-4 h-4 mr-1" />
                        Arsenal
                      </Button>
                    )}
                    {ciclo.is_bowie_dick && !ciclo.teste_bowie_dick && (
                      <Button
                        data-testid={`btn-bowie-dick-${ciclo.id}`}
                        size="sm"
                        onClick={() => handleOpenBowieDick(ciclo.id)}
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        title="Upload do Teste Bowie-Dick"
                      >
                        <TestTube2 className="w-4 h-4 mr-1" />
                        B&D
                      </Button>
                    )}
                    <Button
                      data-testid={`btn-horarios-${ciclo.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenHorarios(ciclo)}
                      title="Definir Hora de Início/Fim"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Clock className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-testes-${ciclo.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenTestes(ciclo.id)}
                      title="Upload de Testes"
                      className="text-purple-600 hover:text-purple-700"
                    >
                      <FlaskConical className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-clientes-pdf-${ciclo.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenClientesPDF(ciclo)}
                      title="PDFs por Cliente / WhatsApp"
                      className="text-green-600 hover:text-green-700"
                    >
                      <Users className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-pdf-${ciclo.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadPDF(ciclo.id)}
                      title="Baixar PDF Completo"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-qr-${ciclo.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleShowQR(ciclo)}
                    >
                      <QrCode className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-etiqueta-${ciclo.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEtiquetaCiclo(ciclo);
                        setEtiquetaQuantidade(1);
                        setShowEtiquetaDialog(true);
                      }}
                      title="Imprimir Etiqueta (50x30mm)"
                      className="text-green-600 hover:text-green-700 border-green-300 hover:bg-green-50"
                    >
                      <Tag className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-print-${ciclo.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenPrintDialog(ciclo.id)}
                      title="Imprimir Etiquetas (Argox/Zebra)"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    {isAdmin && (
                      <Button
                        data-testid={`btn-excluir-ciclo-${ciclo.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteCiclo(ciclo.id)}
                        title="Excluir este ciclo"
                        className="text-red-600 hover:text-red-700 border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {ciclos.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            Nenhum ciclo registrado
          </div>
        )}
      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>QR Code - {selectedCiclo?.numero_ciclo}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4">
            {selectedCiclo && (
              <>
                <ZebraPreview
                  ciclo={selectedCiclo}
                  cliente={clientes.find(c => c.id === selectedCiclo.cliente_id)}
                />
                <div className="text-sm text-slate-600 text-center">
                  <p>URL de consulta:</p>
                  <p className="font-mono text-xs mt-1 break-all">
                    {window.location.origin}/consulta/{selectedCiclo.id}
                  </p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Horários Dialog */}
      <Dialog open={showHorariosDialog} onOpenChange={setShowHorariosDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Horários do Ciclo
            </DialogTitle>
          </DialogHeader>
          {horariosCiclo && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg px-3 py-2 text-sm">
                <span className="font-medium text-blue-800">{horariosCiclo.numero_ciclo}</span>
              </div>
              <div>
                <Label htmlFor="edit-hora-inicio">Hora de Início</Label>
                <Input
                  data-testid="input-edit-hora-inicio"
                  id="edit-hora-inicio"
                  type="time"
                  value={horariosEdit.hora_inicio}
                  onChange={(e) => setHorariosEdit(prev => ({ ...prev, hora_inicio: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-hora-fim">Hora de Fim</Label>
                <Input
                  data-testid="input-edit-hora-fim"
                  id="edit-hora-fim"
                  type="time"
                  value={horariosEdit.hora_fim}
                  onChange={(e) => setHorariosEdit(prev => ({ ...prev, hora_fim: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowHorariosDialog(false)}>Cancelar</Button>
                <Button onClick={handleSaveHorarios} data-testid="btn-salvar-horarios">
                  Salvar Horários
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Testes Biológicos e Químicos Dialog */}
      <Dialog open={showTestesDialog} onOpenChange={setShowTestesDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-purple-600" />
              Upload de Testes - Análise por IA
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Teste Biológico */}
            <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
              <div className="flex items-center gap-2 mb-3">
                <TestTube2 className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-blue-900">Teste Biológico (Indicador de Esporos)</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <Label htmlFor="lote_indicador" className="text-sm">Lote do Indicador</Label>
                  <Input id="lote_indicador" placeholder="Ex: ATT-2024-001" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="tempo_incubacao" className="text-sm">Tempo de Incubação (horas)</Label>
                  <Input id="tempo_incubacao" type="number" onFocus={(e) => e.target.select()} defaultValue={48} className="mt-1" />
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleUploadTesteBiologico}
                    className="hidden"
                    disabled={uploadingTeste}
                  />
                  <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                    {uploadingTeste ? (
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    ) : (
                      <Upload className="w-5 h-5 text-blue-600" />
                    )}
                    <span className="text-sm text-blue-700">
                      {uploadingTeste ? 'Analisando imagem...' : 'Clique para enviar imagem do indicador biológico'}
                    </span>
                  </div>
                </label>
              </div>
              
              {testeResults.biologico && (
                <div className={`mt-3 p-3 rounded-lg ${testeResults.biologico.conformidade ? 'bg-green-100' : 'bg-red-100'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {testeResults.biologico.conformidade ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={`font-semibold ${testeResults.biologico.conformidade ? 'text-green-800' : 'text-red-800'}`}>
                      {testeResults.biologico.resultado === 'negativo' ? 'NEGATIVO (Aprovado)' : 'POSITIVO (Reprovado)'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700">{testeResults.biologico.analise_ia}</p>
                </div>
              )}
            </div>

            {/* Teste Químico */}
            <div className="p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
              <div className="flex items-center gap-2 mb-3">
                <FlaskConical className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-purple-900">Teste Químico (Integrador)</h3>
              </div>
              
              <div className="mb-3">
                <Label htmlFor="classe_indicador" className="text-sm">Classe do Indicador</Label>
                <select id="classe_indicador" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
                  <option value="1">Classe 1 - Indicador de Processo</option>
                  <option value="2">Classe 2 - Teste Específico</option>
                  <option value="3">Classe 3 - Parâmetro Único</option>
                  <option value="4">Classe 4 - Multiparamétrico</option>
                  <option value="5">Classe 5 - Integrador</option>
                  <option value="6">Classe 6 - Emulador</option>
                </select>
              </div>
              
              <div className="flex items-center gap-3">
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleUploadTesteQuimico}
                    className="hidden"
                    disabled={uploadingTeste}
                  />
                  <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors">
                    {uploadingTeste ? (
                      <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                    ) : (
                      <Upload className="w-5 h-5 text-purple-600" />
                    )}
                    <span className="text-sm text-purple-700">
                      {uploadingTeste ? 'Analisando imagem...' : 'Clique para enviar imagem do indicador químico'}
                    </span>
                  </div>
                </label>
              </div>
              
              {testeResults.quimico && (
                <div className={`mt-3 p-3 rounded-lg ${testeResults.quimico.conformidade ? 'bg-green-100' : 'bg-red-100'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {testeResults.quimico.conformidade ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={`font-semibold ${testeResults.quimico.conformidade ? 'text-green-800' : 'text-red-800'}`}>
                      {testeResults.quimico.resultado === 'aprovado' ? 'APROVADO' : 'REPROVADO'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700">{testeResults.quimico.analise_ia}</p>
                </div>
              )}
            </div>

            <p className="text-xs text-slate-500 text-center">
              A IA analisa as imagens dos indicadores e verifica automaticamente se os parâmetros de esterilização foram atingidos.
              Os resultados serão incluídos no PDF do ciclo.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog para enviar PDFs por Cliente */}
      <Dialog open={showClientesDialog} onOpenChange={setShowClientesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              Enviar Comprovantes por Cliente
            </DialogTitle>
          </DialogHeader>
          
          {selectedCicloClientes && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Ciclo: <strong>{selectedCicloClientes.numero_ciclo}</strong>
              </p>
              
              <div className="space-y-3">
                {selectedCicloClientes.clientes_ciclo?.map((cc, idx) => {
                  const cliente = clientes.find(c => c.id === cc.cliente_id);
                  return cliente ? (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div>
                        <p className="font-medium text-slate-900">{cliente.nome}</p>
                        <p className="text-xs text-slate-500">{cliente.telefone || 'Sem telefone'}</p>
                        <p className="text-xs text-slate-500">{cc.materiais_ids?.length || 0} materiais</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadClientePDF(selectedCicloClientes.id, cliente.id, cliente.nome)}
                          title="Baixar PDF"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleSendWhatsApp(selectedCicloClientes.id, cliente.nome, cliente.telefone)}
                          title="Enviar WhatsApp"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
              
              {(!selectedCicloClientes.clientes_ciclo || selectedCicloClientes.clientes_ciclo.length === 0) && (
                <p className="text-center text-slate-500 py-4">
                  Nenhum cliente associado a este ciclo
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bowie-Dick Test Dialog */}
      <Dialog open={showBowieDickDialog} onOpenChange={setShowBowieDickDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TestTube2 className="w-5 h-5 text-amber-600" />
              Teste Bowie-Dick
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="font-semibold text-amber-900 mb-2">O que é o Teste Bowie-Dick?</h4>
              <p className="text-sm text-amber-800">
                O teste Bowie-Dick verifica a remoção de ar e penetração de vapor em autoclaves a vácuo.
                É obrigatório no primeiro ciclo do dia conforme RDC 15 da ANVISA.
              </p>
            </div>

            <div>
              <Label htmlFor="bowie_lote">Lote do Indicador</Label>
              <Input
                id="bowie_lote"
                placeholder="Ex: LOT2024-001"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Imagem do Teste Bowie-Dick</Label>
              <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadBowieDick}
                  className="hidden"
                  id="bowie-dick-upload"
                  disabled={uploadingBowieDick}
                />
                <label
                  htmlFor="bowie-dick-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  {uploadingBowieDick ? (
                    <>
                      <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                      <span className="text-sm text-amber-600 mt-2">Analisando com IA...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400" />
                      <span className="text-sm text-slate-600 mt-2">Clique para enviar imagem</span>
                      <span className="text-xs text-slate-400">PNG, JPG até 10MB</span>
                    </>
                  )}
                </label>
              </div>
            </div>

            {bowieDickResult && (
              <div className={`p-4 rounded-lg ${bowieDickResult.aprovado ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {bowieDickResult.aprovado ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-bold ${bowieDickResult.aprovado ? 'text-green-800' : 'text-red-800'}`}>
                    {bowieDickResult.aprovado ? 'APROVADO' : 'REPROVADO'}
                  </span>
                </div>
                <p className="text-sm text-slate-700">{bowieDickResult.analise_ia}</p>
                {bowieDickResult.recomendacoes && bowieDickResult.recomendacoes.length > 0 && (
                  <ul className="mt-2 text-xs text-slate-600 list-disc list-inside">
                    {bowieDickResult.recomendacoes.map((rec, i) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Quantity Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-600" />
              Imprimir Etiquetas
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Printer Selection */}
            <div>
              <Label>Tipo de Arquivo</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setPrinterType('pdf')}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                    printerType === 'pdf' 
                      ? 'border-red-500 bg-red-50 text-red-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="text-center">
                    <FileText className="w-6 h-6 mx-auto mb-1" />
                    PDF
                    <p className="text-xs text-slate-500 font-normal">Universal</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPrinterType('argox')}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                    printerType === 'argox' 
                      ? 'border-orange-500 bg-orange-50 text-orange-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="text-center">
                    <Printer className="w-6 h-6 mx-auto mb-1" />
                    Argox
                    <p className="text-xs text-slate-500 font-normal">TSPL (.prn)</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPrinterType('zebra')}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                    printerType === 'zebra' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="text-center">
                    <Printer className="w-6 h-6 mx-auto mb-1" />
                    Zebra
                    <p className="text-xs text-slate-500 font-normal">ZPL (.zpl)</p>
                  </div>
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="print_quantity">Quantidade de Etiquetas</Label>
              <div className="flex items-center gap-3 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPrintQuantity(Math.max(1, printQuantity - 1))}
                  disabled={printQuantity <= 1}
                >
                  -
                </Button>
                <Input
                  id="print_quantity"
                  type="number" onFocus={(e) => e.target.select()}
                  min="1"
                  max="100"
                  value={printQuantity}
                  onChange={(e) => setPrintQuantity(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                  className="w-20 text-center"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPrintQuantity(Math.min(100, printQuantity + 1))}
                  disabled={printQuantity >= 100}
                >
                  +
                </Button>
              </div>
              {printerType === 'argox' && (
                <div className="space-y-3">
                  {!qzConnected ? (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm text-orange-800 mb-2 font-medium">Impressão Direta via QZ Tray</p>
                      <p className="text-xs text-orange-600 mb-3">
                        Conecte ao QZ Tray para imprimir direto na Argox sem baixar arquivos.
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleConnectQZ}
                        disabled={qzLoading}
                        className="bg-orange-600 hover:bg-orange-700 w-full"
                        data-testid="connect-qz-btn"
                      >
                        {qzLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Wifi className="w-4 h-4 mr-2" />
                        )}
                        {qzLoading ? 'Conectando...' : 'Conectar QZ Tray'}
                      </Button>
                      <p className="text-xs text-slate-500 mt-2">
                        Não tem o QZ Tray? <a href="https://qz.io/download" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Baixe aqui (gratuito)</a>
                      </p>
                      <div className="border-t border-orange-200 mt-3 pt-2">
                        <p className="text-xs text-slate-500">
                          Ou imprima sem QZ Tray: o arquivo .PRN será baixado para envio manual.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <p className="text-sm text-green-800 font-medium">QZ Tray Conectado</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-600 block mb-1">Impressora:</label>
                        <select
                          value={selectedPrinter}
                          onChange={(e) => setSelectedPrinter(e.target.value)}
                          className="w-full text-sm border rounded-md px-2 py-1.5 bg-white"
                          data-testid="printer-select"
                        >
                          <option value="">Selecione a impressora</option>
                          {qzPrinters.map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                      {selectedPrinter && (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleTestPrint}
                            className="w-full text-xs border-green-300 text-green-700 hover:bg-green-100"
                            data-testid="test-print-btn"
                          >
                            <Printer className="w-3 h-3 mr-1" />
                            Teste de Impressão
                          </Button>
                          <p className="text-xs text-green-600">
                            Pronto! A etiqueta será impressa na <strong>{selectedPrinter}</strong>
                          </p>
                        </>
                      )}
                      <details className="mt-1">
                        <summary className="text-xs text-amber-700 cursor-pointer font-medium">
                          Não imprimiu? Veja como resolver
                        </summary>
                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 space-y-1">
                          <p className="font-semibold">Dicas de diagnóstico:</p>
                          <ol className="list-decimal pl-4 space-y-0.5">
                            <li>Selecione a impressora <strong>"Argox OS-2140 ppla"</strong> (mesmo nome do BarTender)</li>
                            <li>Verifique se a impressora está ligada e conectada via USB</li>
                            <li>Verifique se tem papel/etiqueta carregada</li>
                            <li>Clique em "Teste de Impressão" primeiro</li>
                            <li>Se não funcionar, tente reiniciar o QZ Tray e a impressora</li>
                          </ol>
                          <p className="mt-1 text-amber-600">
                            A impressão agora usa o <strong>driver da impressora</strong> (como o BarTender), não mais comandos raw.
                          </p>
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              )}
              {printerType === 'pdf' && (
                <p className="text-xs text-slate-500 mt-2">
                  * PDF com QR Code - ideal para imprimir em qualquer impressora
                </p>
              )}
              {printerType === 'zebra' && (
                <div className="space-y-3">
                  {!qzConnected ? (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800 mb-2 font-medium">Impressão Direta via QZ Tray</p>
                      <p className="text-xs text-blue-600 mb-3">
                        Conecte ao QZ Tray para imprimir direto na Zebra sem baixar arquivos.
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleConnectQZ}
                        disabled={qzLoading}
                        className="bg-blue-600 hover:bg-blue-700 w-full"
                        data-testid="connect-qz-zebra-btn"
                      >
                        {qzLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Wifi className="w-4 h-4 mr-2" />
                        )}
                        {qzLoading ? 'Conectando...' : 'Conectar QZ Tray'}
                      </Button>
                      <p className="text-xs text-slate-500 mt-2">
                        Não tem? <a href="https://qz.io/download" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Baixe aqui</a> | Ou o arquivo .ZPL será baixado.
                      </p>
                    </div>
                  ) : (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        <p className="text-sm text-green-800 font-medium">QZ Tray Conectado</p>
                      </div>
                      <div className="mt-2">
                        <label className="text-xs text-slate-600 block mb-1">Impressora:</label>
                        <select
                          value={selectedPrinter}
                          onChange={(e) => setSelectedPrinter(e.target.value)}
                          className="w-full text-sm border rounded-md px-2 py-1.5 bg-white"
                          data-testid="printer-select-zebra"
                        >
                          <option value="">Selecione a impressora</option>
                          {qzPrinters.map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                      {selectedPrinter && (
                        <p className="text-xs text-green-600 mt-2">
                          Pronto! Etiquetas serão enviadas para <strong>{selectedPrinter}</strong>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={`flex items-center gap-2 p-3 rounded-lg border ${
              printerType === 'pdf' 
                ? 'bg-red-50 border-red-200' 
                : printerType === 'argox' 
                  ? 'bg-orange-50 border-orange-200' 
                  : 'bg-blue-50 border-blue-200'
            }`}>
              {printerType === 'pdf' ? (
                <FileText className="w-5 h-5 text-red-600" />
              ) : (
                <Printer className={`w-5 h-5 ${printerType === 'argox' ? 'text-orange-600' : 'text-blue-600'}`} />
              )}
              <div>
                <p className={`text-sm font-medium ${
                  printerType === 'pdf' 
                    ? 'text-red-800' 
                    : printerType === 'argox' 
                      ? 'text-orange-800' 
                      : 'text-blue-800'
                }`}>
                  Total: {printQuantity * 2} etiqueta(s)
                </p>
                <p className={`text-xs ${
                  printerType === 'pdf' 
                    ? 'text-red-600' 
                    : printerType === 'argox' 
                      ? 'text-orange-600' 
                      : 'text-blue-600'
                }`}>
                  {printerType === 'pdf' 
                    ? 'Etiquetas 40x25mm com QR Code' 
                    : `${printQuantity} linha(s) x 2 colunas (40x25mm)`}
                </p>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowPrintDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handlePrint} 
                className={
                  printerType === 'pdf' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : printerType === 'argox' 
                      ? 'bg-orange-600 hover:bg-orange-700' 
                      : 'bg-blue-600 hover:bg-blue-700'
                }
                data-testid="print-execute-btn"
              >
                {printerType === 'pdf' ? (
                  <FileText className="w-4 h-4 mr-2" />
                ) : (
                  <Printer className={`w-4 h-4 mr-2 ${printerType === 'argox' ? 'text-orange-100' : 'text-blue-100'}`} />
                )}
                {printerType === 'pdf' 
                  ? 'Gerar PDF' 
                  : (qzConnected && selectedPrinter ? 'Imprimir Direto' : printerType === 'argox' ? 'Baixar .PRN' : 'Baixar .ZPL')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Etiqueta Preview Dialog - Impressão Direta */}
      <EtiquetaPreviewDialog
        open={showEtiquetaDialog}
        onOpenChange={setShowEtiquetaDialog}
        ciclo={etiquetaCiclo}
        quantidade={etiquetaQuantidade}
        onQuantidadeChange={setEtiquetaQuantidade}
        diasValidade={etiquetaDiasValidade}
        onDiasValidadeChange={setEtiquetaDiasValidade}
      />
    </div>
  );
}